<template>
  <div class="dialog-container d-flex flex-column" :class="{ 'fill-height': $vuetify.breakpoint.xsOnly }">
    <div class="d-flex justify-space-between full-width">
      <div class="dialog-container-title">{{ $t("chat.startConversationModal.title") }}</div>
      <v-btn icon x-small color="primarylight3" @click="close"><v-icon>mdi-close</v-icon></v-btn>
    </div>
    <div class="full-width">
      <div v-if="userIsPatient" class="dialog-container-text mt-6 mb-3">
        {{ $t("chat.startConversationModal.choosePractitioner") }}
      </div>
      <div v-if="userIsPractitioner" class="dialog-container-text mt-6 mb-3">
        {{ $t("chat.startConversationModal.choosePatient") }}
      </div>
    </div>
    <div class="mb-4">
      <v-autocomplete
        outlined
        dense
        height="46"
        v-model="participantId"
        :items="participantOptions"
        append-icon
        hide-details
        :disabled="userIsPatient"
        :loading="usersLoading"
      >
        <template v-slot:selection="data">
          <v-chip color="white">
            <Avatar :userId="data.item.value" />
            {{ data.item.name }}
          </v-chip>
        </template>
        <template v-slot:item="data">
          <template>
            <v-list-item-avatar>
              <Avatar :userId="data.item.value" />
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title v-html="data.item.name"></v-list-item-title>
            </v-list-item-content>
          </template>
        </template>
      </v-autocomplete>
    </div>
    <div class="dialog-container-chief d-flex align-center mb-4">
      <div class="d-flex align-center dialog-container-text dialog-container-chief-wd mr-1">
        <span>{{ $t("visits.chiefComplaint") }}</span>
      </div>
      <div class="dialog-container-chief-select">
        <v-select
          v-model="chiefComplaint"
          :items="chiefComplaints"
          outlined
          dense
          height="46"
          hide-details
          append-icon
          class="full-width"
        />
      </div>
    </div>
    <div class="mb-7">
      <v-textarea
        v-model="visitReasonNote"
        :label="$t('chat.visitReason')"
        auto-grow
        outlined
        rows="5"
        row-height="20"
        hide-details
      />
    </div>
    <div v-if="userIsPatient" class="d-flex align-center mb-7">
      <v-checkbox v-model="checkbox" dense hide-details class="mr-1 mt-0 pt-0" />
      <i18n path="chat.startConversationModal.confirmTxt" tag="span" class="dialog-container-subtext">
        <span class="dialog-container-link" @click.prevent="medicalInformationLink">{{
          $t("chat.startConversationModal.medicalInformation")
        }}</span>
      </i18n>
    </div>
    <div>
      <v-btn color="primary" depressed block :disabled="!enabledFinish" :loading="finishLoading" @click="finish">
        {{ $t("chat.startConversationModal.start") }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";

import Avatar from "@/components/Avatar/Index.vue";
import { snackBarEventBus, snackBarEventName } from "@/eventBuses/snackBar.eventBus";

export default Vue.extend({
  name: "StartNewConversation",
  components: {
    Avatar,
  },
  data() {
    return {
      participantId: null,
      checkbox: false,
      chiefComplaint: null,
      visitReasonNote: null,
      finishLoading: false,
      usersLoading: false,
    };
  },
  computed: {
    ...mapGetters(["role", "userIsPatient", "userIsPractitioner"]),
    ...mapGetters("patient", ["patient"]),
    ...mapGetters("patients", ["practitionerOwnPatients"]),
    enabledFinish() {
      console.log("this.participantId", this.participantId);
      console.log("this.chiefComplaint", this.chiefComplaint);
      console.log("this.userIsPractitioner", this.userIsPractitioner);
      console.log("this.checkbox", this.checkbox);
      return (this.checkbox || this.userIsPractitioner) && this.chiefComplaint && this.participantId;
    },
    chiefComplaints() {
      const chiefComplaints = this.$t("general.chiefComplaints");
      if (!chiefComplaints) {
        return [];
      }
      return Object.keys(chiefComplaints).map((key) => ({ text: chiefComplaints[key], value: key }));
    },
    participantOptions() {
      if (this.userIsPatient) {
        return this.practitioners;
      } else {
        return this.patients;
      }
    },
    practitioners() {
      if (!this.patient?.practitioner) {
        return [];
      }
      const { firstName, lastName, id } = this.patient.practitioner;
      return [
        {
          name: `${firstName} ${lastName}`,
          value: id,
        },
      ];
    },
    patients() {
      return this.practitionerOwnPatients.map(({ firstName, lastName, id }) => ({
        name: `${firstName} ${lastName}`,
        value: id,
      }));
    },
  },
  methods: {
    ...mapActions("patients", ["getAllByPractitionerId"]),
    ...mapActions("patient", ["getPatientProfile"]),
    ...mapActions("chat", ["createChat"]),
    close() {
      this.checkbox = false;
      this.friends = null;
      this.chiefComplaint = null;
      this.$emit("close");
    },
    async finish() {
      this.$gtag.event("click", { method: "Start Chat" });
      this.finishLoading = true;
      const chatCreateRequest = {
        participantId: this.participantId,
        information: {
          chiefComplaint: this.chiefComplaint,
          complaintDescription: this.visitReasonNote,
        },
      };
      await this.createChat(chatCreateRequest);
      this.finishLoading = false;
      this.close();
    },
    async medicalInformationLink() {
      const routeData = this.$router.resolve({ name: "patientSettings" });
      window.open(routeData.href, "_blank");
    },
  },
  async mounted() {
    this.usersLoading = true;
    try {
      if (this.userIsPractitioner) {
        await this.getAllByPractitionerId();
      } else if (this.userIsPatient) {
        this.participantId = this.patient?.practitioner?.id || null;
        await this.getPatientProfile();
      } else {
        snackBarEventBus.$emit(snackBarEventName, {
          message: "User role is not appropriate for that operation!",
          type: "error",
        });
      }
    } catch (err) {
      snackBarEventBus.$emit(snackBarEventName, { message: err.message || err, type: "error" });
    } finally {
      this.usersLoading = false;
    }
  },
  watch: {
    patient() {
      if (this.userIsPatient) {
        if (!this.patient?.practitioner) {
          return;
        }
        this.participantId = this.patient.practitioner.id;
      }
    },
  },
});
</script>

<style scoped lang="scss">
.dialog-container {
  background-color: white;
  padding: 25px 31px 25px 25px;
  &-title {
    font-weight: 500;
    font-size: 16px;
    color: $primaryblack;
  }
  &-text {
    font-weight: 500;
    font-size: 14px;
    color: $primaryblack2;
    mix-blend-mode: normal;
    opacity: 0.88;
  }
  &-subtext {
    font-size: 13px;
    color: $primaryblack2;
    mix-blend-mode: normal;
    opacity: 0.88;
  }
  &-chief {
    height: 46px;
    &-wd {
      width: 120px;
    }
    &-select {
      width: 70%;
    }
  }
  &-link {
    font-weight: 500;
    color: $primary;
    cursor: pointer;
  }
}
</style>
